import React, { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CircleCheckIcon, CircleXIcon, ChevronDownIcon, ClockIcon } from 'lucide-react';

import dayjs from 'utils/extendedDayJS';
import { queryClient } from '@api/queryClient';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { getGraphQLErrorMessage } from 'utils/graphqlErrors';
import { Button } from '@epoch-ui/components/Button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel
} from '@epoch-ui/components/AlertDialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '@epoch-ui/components/DropdownMenu';
import { cancelledEventApi } from '@event-editor/api/cancelled-event';
import { draftEventApi } from '@event-editor/api/draft-event';
import { publishedEventApi } from '@event-editor/api/published-event';
import { scheduledPublishApi } from '@event-editor/api/scheduled-publish';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import {
  useDeleteDraftEventModal,
  useCancelEventModal,
  useSchedulePublishModal,
  usePublishErrorsModal,
  useCreateDraftSessionSheet
} from '@event-editor/hooks/eventEditorState';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { useSessionsQuery } from '@event-editor/hooks/useSessionsQuery';
import { EventPublishScheduleStatus } from 'types/event.types';
import { PRETTY_DATE_TIME_TZ_FORMAT } from 'constants/dates';
import { ZOOM_SYNC_FAILED_ERROR } from 'constants/strings';

import { validatePublish } from './validatePublish';

type EventStatusChangeDropdownOptionsProps = {
  restoreEventHandler: () => void;
  publishEventHandler: () => void;
  unpublishEventHandler: () => void;
  removeScheduleHandler: () => void;
  isRemovingSchedule: boolean;
};

const PUBLISH_FAILED_FALLBACK = "Event couldn't be published. Please try again later.";

const EventStatusChangeDropdownOptions: React.FC<EventStatusChangeDropdownOptionsProps> = ({
  restoreEventHandler,
  publishEventHandler,
  unpublishEventHandler,
  removeScheduleHandler,
  isRemovingSchedule
}) => {
  const event = useEditableEvent();

  const cancelEventModal = useCancelEventModal();
  const deleteDraftEventModal = useDeleteDraftEventModal();
  const schedulePublishModal = useSchedulePublishModal();

  const isCancelled = !!event.scheduledDeletionAt;
  const hasPublishSchedule =
    event.publishSchedule && event.publishSchedule.status === EventPublishScheduleStatus.SCHEDULED;

  if (isCancelled) {
    return (
      <DropdownMenuItem disabled={!event.canRestoreFromCancellation} onClick={restoreEventHandler}>
        Restore event
      </DropdownMenuItem>
    );
  }

  if (event.isPublished) {
    return (
      <>
        <DropdownMenuItem disabled={(event.totalGuests ?? 0) > 0} onClick={unpublishEventHandler}>
          Revert to draft
        </DropdownMenuItem>
        <DropdownMenuItem
          className="tw-text-destructive-foreground"
          onClick={cancelEventModal.open}
        >
          Cancel event
        </DropdownMenuItem>
      </>
    );
  }

  // Draft event with publish schedule
  if (hasPublishSchedule) {
    const scheduledAtFormatted = dayjs.formatPrettyDateTime({
      dateTime: event.publishSchedule!.scheduledAt,
      timezoneCode: event.publishSchedule!.timezoneCode,
      customFormat: PRETTY_DATE_TIME_TZ_FORMAT
    });

    return (
      <>
        <DropdownMenuLabel className="tw-flex tw-flex-col tw-gap-0.5">
          <span className="tw-font-medium">Publish scheduled</span>
          <span className="tw-text-xs tw-font-normal tw-text-muted-foreground">
            {scheduledAtFormatted}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={publishEventHandler}>Publish now</DropdownMenuItem>
        <DropdownMenuItem onClick={() => schedulePublishModal.open({ isReschedule: true })}>
          Reschedule
        </DropdownMenuItem>
        <DropdownMenuItem disabled={isRemovingSchedule} onClick={removeScheduleHandler}>
          Remove schedule
        </DropdownMenuItem>
        <DropdownMenuItem
          className="tw-text-destructive-foreground"
          onClick={deleteDraftEventModal.open}
        >
          Delete draft event
        </DropdownMenuItem>
      </>
    );
  }

  // Draft event without schedule
  return (
    <>
      <DropdownMenuItem onClick={publishEventHandler}>Publish</DropdownMenuItem>
      <DropdownMenuItem onClick={() => schedulePublishModal.open()}>
        Schedule publish
      </DropdownMenuItem>
      <DropdownMenuItem
        className="tw-text-destructive-foreground"
        onClick={deleteDraftEventModal.open}
      >
        Delete draft event
      </DropdownMenuItem>
    </>
  );
};

const EventStatusChangeDropdown: React.FC = () => {
  const event = useEditableEvent();
  const sessionsQuery = useSessionsQuery(event);
  const createDraftSessionSheet = useCreateDraftSessionSheet();
  const { showToast } = useToaster();
  const { showBanner: showPublishErrorsBanner, dismissBanner: dismissPublishErrorsBanner } =
    usePublishErrorsModal();

  const [isSessionsRequiredModalOpen, setIsSessionsRequiredModalOpen] = useState(false);

  // Publish event mutation
  const publishEventMutation = useMutation({
    mutationFn: draftEventApi.publish,
    onSuccess: async (result) => {
      if (result.ok) {
        await queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        if (result.zoomSyncFailed) {
          showToast(ZOOM_SYNC_FAILED_ERROR, { variant: ToasterVariant.ERROR, keepOpen: true });
        } else {
          showToast('Event published successfully.');
        }
      } else {
        showToast(PUBLISH_FAILED_FALLBACK, { variant: ToasterVariant.ERROR });
      }
    },
    onError: (error) => {
      // The API rejects a publish with the specific setting that's outstanding — show
      // that rather than a generic retry prompt the admin can't act on.
      showToast(getGraphQLErrorMessage(error, PUBLISH_FAILED_FALLBACK), {
        variant: ToasterVariant.ERROR
      });
    }
  });

  // Restore event mutation
  const restoreEventMutation = useMutation({
    mutationFn: cancelledEventApi.restore,
    onSuccess: async (result) => {
      if (result.ok) {
        await queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        showToast('Event restored successfully.');
      } else {
        showToast('Failed to restore event', { variant: ToasterVariant.ERROR });
      }
    },
    onError: () => {
      showToast('Failed to restore event', { variant: ToasterVariant.ERROR });
    }
  });

  // Unpublish event mutation
  const unpublishEventMutation = useMutation({
    mutationFn: publishedEventApi.unpublish,
    onSuccess: async (result) => {
      if (result.ok) {
        await queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        showToast('Event unpublished successfully.');
      } else {
        showToast('Failed to unpublish event', { variant: ToasterVariant.ERROR });
      }
    },
    onError: () => {
      showToast('Failed to unpublish event', { variant: ToasterVariant.ERROR });
    }
  });

  // Remove publish schedule mutation
  const removeScheduleMutation = useMutation({
    mutationFn: scheduledPublishApi.remove,
    onSuccess: async (result) => {
      if (result.ok) {
        queryClient.setQueryData(eventEditorQueryKeys.event(event.id!), (oldData: any) =>
          oldData
            ? {
                event: {
                  ...oldData.event,
                  publishSchedule: null
                }
              }
            : oldData
        );
        showToast('Publish schedule removed successfully.');
      } else {
        showToast('Failed to remove publish schedule', { variant: ToasterVariant.ERROR });
      }
    },
    onError: () => {
      showToast('Failed to remove publish schedule', { variant: ToasterVariant.ERROR });
    }
  });

  const isCancelled = !!event.scheduledDeletionAt;
  const hasPublishSchedule =
    event.publishSchedule && event.publishSchedule.status === EventPublishScheduleStatus.SCHEDULED;
  const isPending =
    restoreEventMutation.isPending ||
    publishEventMutation.isPending ||
    unpublishEventMutation.isPending ||
    removeScheduleMutation.isPending;

  const statusLabel: React.ReactNode = useMemo(() => {
    if (isCancelled) {
      return (
        <>
          <CircleXIcon size={16} className="tw-mr-1 tw-text-destructive-foreground" />
          Cancelled
        </>
      );
    }

    if (event.isPublished) {
      return (
        <>
          <CircleCheckIcon
            size={16}
            fill="green"
            color="white"
            strokeWidth={1}
            className="tw-mr-1"
          />
          Published
        </>
      );
    }

    // Draft with publish schedule
    if (hasPublishSchedule) {
      return (
        <>
          <ClockIcon size={16} className="tw-mr-1" />
          Scheduled
        </>
      );
    }

    return 'Draft';
  }, [isCancelled, event.isPublished, hasPublishSchedule]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={event.hasEnded} data-testid="status-dropdown-trigger">
        <Button
          variant="outline"
          size="sm"
          className="tw-rounded-[4px]"
          loading={isPending}
          disabled={event.hasEnded || isPending}
        >
          {statusLabel}
          <ChevronDownIcon size={16} className="tw-ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <EventStatusChangeDropdownOptions
          restoreEventHandler={() => restoreEventMutation.mutate(event.id!)}
          publishEventHandler={() => {
            if (sessionsQuery.data?.length === 0) {
              setIsSessionsRequiredModalOpen(true);
              return;
            }

            // Clear any existing publish errors
            dismissPublishErrorsBanner();

            // Validate before publishing
            const validationErrors = validatePublish(event);
            if (validationErrors.length > 0) {
              showPublishErrorsBanner(validationErrors);
              return;
            }

            // Proceed with publish
            publishEventMutation.mutate(event.id!);
          }}
          unpublishEventHandler={() => unpublishEventMutation.mutate(event.id!)}
          removeScheduleHandler={() => removeScheduleMutation.mutate(event.id!)}
          isRemovingSchedule={removeScheduleMutation.isPending}
        />
      </DropdownMenuContent>

      <AlertDialog open={isSessionsRequiredModalOpen} onOpenChange={setIsSessionsRequiredModalOpen}>
        <AlertDialogContent className="tw-max-w-[400px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Sessions required</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            To publish your multi-session event, you must add sessions to the event.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel className="tw-rounded-xl" variant="secondary">
              Return to draft
            </AlertDialogCancel>
            <AlertDialogAction
              className="tw-rounded-xl"
              onClick={() => createDraftSessionSheet.open()}
            >
              Add session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DropdownMenu>
  );
};

export { EventStatusChangeDropdown };
